import { useCallback, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import LeadInstantResponseSettings from './LeadInstantResponseSettings'
import DisconnectGBMConfirmationModal from '../DisconnectGBMConfirmationModal'
import { LocationsResource } from 'src/client'
import { useInstantResponder } from 'src/containers/Settings/InstantResponders/hooks'
import useAccountContext from 'src/contexts/AccountContext'
import useModalNotificationsContext from 'src/contexts/ModalNotificationsContext'
import Constants from 'src/lib/Constants'
import { getActiveLocation, generateLocationUrl } from 'src/utils'
import { UseLocationRouteParams } from 'src/utils/interfaces'
import logger from 'src/utils/logger'
import WarningCard from '../../common/WarningCard'

interface GBMInstantResponseSettingsProps {
  customIR: string | null
  isConnected: boolean
  isEnabled: boolean
  integrationStatus?: string
  integrationUpdatedAt: string | null
}

const GBMInstantResponseSettings: React.FC<GBMInstantResponseSettingsProps> = ({
  isConnected,
  isEnabled,
  customIR,
  integrationStatus,
  integrationUpdatedAt,
}) => {
  const { locationId, merchantId } = useParams<UseLocationRouteParams>()
  const { locations, handleRefetchUserLocations } = useAccountContext()
  const { showModal, closeModal } = useModalNotificationsContext()
  const activeLocation = getActiveLocation(locations, locationId)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const navigate = useNavigate()

  const { customIRMessage, isToggleEnabled, onToggle, onUpdateMessage } =
    useInstantResponder({
      customIR,
      isEnabled,
      locationId: activeLocation?.locationId,
      refetchLocationFn: handleRefetchUserLocations,
      source: 'googleMessages',
    })

  const handleConnect = () => {
    const googleMessagesConnectionWizardUrl = generateLocationUrl(
      merchantId!,
      locationId!,
      '/integrations/connect#google-messages'
    )

    navigate(googleMessagesConnectionWizardUrl)
  }

  const handleConfirmDisconnect = useCallback(async () => {
    if (!activeLocation) return

    closeModal()

    setIsDisconnecting(true)

    try {
      await LocationsResource.toggleFeatureCode({
        enable: false,
        featureCode: Constants.FEATURE_CODES.syncGoogleMessages,
        locationId: activeLocation.locationId,
      })

      await handleRefetchUserLocations()

      toast.success('Successfully disconnected')
    } catch (error) {
      logger.debug('Google Messages Responder - disconnect Google Messages', {
        error,
      })
      toast.error(
        'There was an error trying to disconnect you from Google. Please try again.'
      )
    } finally {
      setIsDisconnecting(false)
    }
  }, [activeLocation, closeModal, handleRefetchUserLocations])

  const handleDisconnect = useCallback(() => {
    showModal({
      title: 'Disconnect Google Business Messages',
      headerColor: 'critical',
      hideActionButtons: false,
      modalActionsOptions: {
        callToAction: {
          label: 'Disconnect',
          action: 'danger',
          onClick: handleConfirmDisconnect,
        },
      },
      customBody: <DisconnectGBMConfirmationModal />,
      height: 'auto',
    })
  }, [handleConfirmDisconnect, showModal])

  return (
    <>
      <WarningCard content="Google is discontinuing Google Business Messages. This feature will be removed on July 31, 2024" />
      <LeadInstantResponseSettings
        connectable
        connected={isConnected}
        defaultMessage={
          Constants.INSTANT_RESPONSES.googleMessages.defaultMessage
        }
        enabled={isToggleEnabled}
        isDisconnecting={isDisconnecting}
        leadSourceName="Google Business Messages"
        message={customIRMessage}
        onClickConnect={handleConnect}
        onClickDisconnect={handleDisconnect}
        onEnablementChange={onToggle}
        onUpdateMessage={onUpdateMessage}
        integrationStatus={integrationStatus}
        integrationUpdatedAt={integrationUpdatedAt}
      />
    </>
  )
}

export default GBMInstantResponseSettings
